<template lang="pug">
  #featured-challenge-large
    WtTile(parent)
      WtTile(child :col="12" :is-loading="!currentFeaturedChallengeLoaded")
        img.featured-ribbon(:src="require(`@/assets/icons/featured_challenge_ribbon.svg`)")
        .columns.is-multiline
          .challenge-image-column.column.is-narrow
            RouterLink(
              v-if="featuredChallenge.id"
              :to="{ name: 'Challenge', params: { challengeId: featuredChallenge.id } }"
            )
              figure.image.is-96x96.challenge-figure
                img.is-rounded.challenge-image(
                  :src="challengeImage"
                  @error="replaceWithDefaultImage"
                )
          .challenge-meta.column
            div.challenge-meta-content
              .challenge-meta-header
                .is-size-5.is-flex.is-align-items-center.header-content
                  | {{date(featuredChallenge.startDate, "DATE_FORMAT_SLASH").formatted }} - {{ date(featuredChallenge.endDate, "DATE_FORMAT_SLASH").formatted }}
                  span.mr-5
                  BTag(
                    v-if="isTodayBeforeChallengeStart(featuredChallenge)"
                  ).is-rounded.days-left-tag.has-text-danger {{daysTillChallengeStart(featuredChallenge)}}
                    span.has-text-grey {{" "}}days till start
                  BTag(
                    v-if="!isTodayBeforeChallengeStart(featuredChallenge) && !isTodayAfterChallengeEnd(featuredChallenge)"
                  ).is-rounded.days-left-tag {{daysRemainingInChallenge(featuredChallenge)}} {{ daysRemainingInChallengeText(featuredChallenge) }}
                  BTag(
                    v-if="isTodayAfterChallengeEnd(featuredChallenge)"
                  ).is-rounded.days-left-tag.has-text-danger {{daysTillTallyText(featuredChallenge)}}
                  ChallengeJoinButton.ml-2(:challenge="featuredChallenge")
              WtTitle(h2 v-if="!_.isEmpty(featuredChallenge)").mb-2
                .title-container
                  RouterLink(
                    :to="{ name: 'Challenge', params: { challengeId: featuredChallenge.id } }"
                  ) {{ featuredChallenge.name }}&nbsp;&nbsp;
              BTag.is-rounded.challenge-type-tag {{challengeType(featuredChallenge) +' Challenge' }}
              p.challenge-description.mt-4.mr-4
                WtDisplayRichTextHTML(:html="featuredChallenge.atStake")
              .stats-column.column
                ChallengeActivityOverview(:challenge="featuredChallenge" isFeatured)
</template>

<script>
import { mapGetters } from "vuex";
import { challengeMixin } from "@/mixins/challengeMixin";
import WtTile from "@@/WtTile/WtTile";
import WtTitle from "@@/WtTitle/WtTitle";
import WtStatRow from "@@/WtStatRow/WtStatRow";
import ChallengeActivityOverview from "@@/Challenge/ChallengeActivityOverview";
import ChallengeJoinButton from "@@/Challenge/ChallengeJoinButton";
import { dateUtilityMixin } from "@/mixins/dateUtilityMixin";
import WtDisplayRichTextHTML from "@@/WtDisplayRichTextHTML/WtDisplayRichTextHTML";

export default {
  name: "FeaturedChallengeLarge",

  components: {
    WtStatRow,
    WtTile,
    WtTitle,
    ChallengeActivityOverview,
    ChallengeJoinButton,
    WtDisplayRichTextHTML
  },

  mixins: [challengeMixin, dateUtilityMixin],

  props: {
    featuredChallenge: {
      type: Object,
      required: true
    }
  },

  computed: {
    ...mapGetters("program", ["coreProgramMeta"]),
    ...mapGetters("program", ["currentFeaturedChallengeLoaded"]),

    unitsLabel() {
      return `Total ${this._.capitalize(this.coreProgramMeta.units)}`;
    },

    description() {
      return this.mapDescription(this.featuredChallenge);
    },

    challengeImage() {
      // Always try to get map.image with the placeholder as fallback
      const mapImage = this._.get(
        this.featuredChallenge,
        "map.image",
        this.mapImagePlaceholder()
      );

      return this.isPreventative
        ? this._.get(this.featuredChallenge, "challengeImage", null) || mapImage
        : mapImage;
    },

    isPreventative() {
      return this._.get(this.featuredChallenge, "isPreventative", false);
    }
  },

  methods: {
    replaceWithDefaultImage(event) {
      event.target.src = this.mapImagePlaceholder();
    }
  }
};
</script>

<style lang="sass" scoped>
#featured-challenge-large
  .title-container
    display: flex
    align-items: center
    white-space: nowrap
    +mobile
      text-align: center
      justify-content: center
      white-space: normal
      height: 100px
  .challenge-image-column
    width: 110px
    +mobile
      width: 100%
      display: flex
      justify-content: center
      align-items: center
      padding: 0
      margin: 0
    .challenge-figure
      +mobile
        width: 150px
        height: 150px
    .challenge-image
      height: 100% // temp until all challenge images are standardized
      padding: 10px
      object-fit: cover
  .column.challenge-meta
    padding-top: 0 // overrides column style
    margin-top: 0.8rem
    +mobile
      padding-left: 10%
      padding-right: 10%
    .challenge-meta-header
      padding-top: .75rem // replaces column style so background-color is applied
      width: 100%
      background-color: $white
      position: sticky
      top: 0
      z-index: 1
      white-space: nowrap
      +mobile
        padding: 0
      .header-content
        +mobile
          flex-direction: column
    .challenge-meta-body
      top: 1rem
      position: relative
      z-index: 0
  .stats-column
    padding: 1rem 2rem 2rem 0
    display: flex
    height: 156px
    /deep/ #activity-overview
      display: flex
      white-space: nowrap
      justify-content: space-between
      align-items: center
      margin: 0
      flex-wrap: wrap
      width: 100%
      .wt-stat-row
        margin: 0
  .card
    border-radius: 0 0 0 6px
  .banner
    border-radius: $radius $radius 0 0
    background-color: $navy
    height: 45px
    margin-bottom: 0
    &-title
      color: $white
      text-transform: uppercase
  .challenge-type-tag
    background-color: $green-lighter
    color: $green-dark
  .challenge-description
    background-color: $grey-lighter
    color: $grey-dark
    border-radius: $radius
    padding: 0 10px
    height: 85px
    overflow: scroll
    max-width: 900px
    +mobile
      width: 100%
      height: 110px
  .days-left-tag
    background-color: $grey-lighter
    color: $grey-dark
    font-size: $size-5
  .featured-ribbon
    position: absolute
    top: 0
    right: 60px
    z-index: 2
</style>
